import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import KpiCard from './KpiCard';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const SVC_COLORS = ['#7F77DD', '#1D9E75', '#378ADD', '#D4537E'];
const SVC_LIGHT = ['#EEEDFE', '#E1F5EE', '#E6F1FB', '#FBEAF0'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SectionHead = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  color: '#9b9b97',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  marginBottom: '10px',
});

const Grid4 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '10px',
  marginBottom: '14px',
});

const Grid2 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px',
  marginBottom: '14px',
});

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  border: '0.5px solid #e0ddd6',
  borderRadius: '12px',
  padding: '16px',
  overflow: 'hidden',
});

const CardTitle = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b6b68',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  marginBottom: '14px',
});

const CHART_GRID = '#e0ddd633';
const CHART_TICK = '#9b9b97';

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.75)',
      padding: 8,
      titleFont: { size: 11 },
      bodyFont: { size: 11 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: CHART_TICK, font: { size: 10 }, maxRotation: 0 },
    },
    y: {
      grid: { color: CHART_GRID },
      ticks: { color: CHART_TICK, font: { size: 10 } },
      border: { dash: [3, 3] },
    },
  },
};

function fmtK(n) {
  return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
}

function getHourLabel(i) {
  if (i % 4 !== 0) return '';
  if (i === 0) return '12am';
  if (i < 12) return i + 'am';
  if (i === 12) return '12pm';
  return (i - 12) + 'pm';
}

export default function UsersTab({ data }) {
  if (!data) return null;
  const d = data;

  const newUsers = d.newUsers || 0;
  const returningUsers = d.returningUsers || 0;
  const totalActive = d.totalActiveUsers || (newUsers + returningUsers);
  const returnRate = d.returnRate || (totalActive > 0 ? Math.round(returningUsers / totalActive * 100) + '%' : '0%');

  // Parse service type data — API uses _id for name
  let services = [];
  if (Array.isArray(d.requestsByServiceType)) {
    services = d.requestsByServiceType.map(item => ({
      name: item.serviceType || item._id || item.name || 'Unknown',
      count: item.count || item.value || 0,
    }));
  }
  const svcTotal = services.reduce((acc, s) => acc + s.count, 0);

  // Parse hourly data — API returns sparse { hour, count } objects, fill all 24 slots
  let byHour = new Array(24).fill(0);
  if (Array.isArray(d.requestsByHour)) {
    d.requestsByHour.forEach(item => {
      if (typeof item === 'number') return; // fallback for flat array
      const h = item.hour;
      if (h >= 0 && h < 24) byHour[h] = item.count || 0;
    });
    // If the array was flat numbers (not objects), use directly
    if (d.requestsByHour.length === 24 && typeof d.requestsByHour[0] === 'number') {
      byHour = d.requestsByHour;
    }
  }

  // Parse day-of-week data — API returns { day, count } objects
  let byDay = [];
  if (Array.isArray(d.requestsByDayOfWeek)) {
    if (typeof d.requestsByDayOfWeek[0] === 'number') {
      byDay = d.requestsByDayOfWeek;
    } else {
      // Map day names to ordered array
      const dayOrder = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6 };
      byDay = new Array(7).fill(0);
      d.requestsByDayOfWeek.forEach(item => {
        const idx = dayOrder[item.day] ?? dayOrder[item._id];
        if (idx !== undefined) byDay[idx] = item.count || 0;
      });
    }
  }

  const serviceChartData = {
    labels: services.map(s => s.name),
    datasets: [{
      data: services.map(s => s.count),
      backgroundColor: services.map((_, i) => SVC_COLORS[i % SVC_COLORS.length] + '33'),
      borderColor: services.map((_, i) => SVC_COLORS[i % SVC_COLORS.length]),
      borderWidth: 1.5,
      borderRadius: 2,
    }],
  };

  const hourChartData = {
    labels: Array.from({ length: 24 }, (_, i) => getHourLabel(i)),
    datasets: [{
      data: byHour,
      backgroundColor: 'rgba(55,138,221,0.2)',
      borderColor: '#378ADD',
      borderWidth: 1.5,
      borderRadius: 2,
    }],
  };

  const dayChartData = {
    labels: DAYS,
    datasets: [{
      data: byDay,
      backgroundColor: byDay.map((_, i) => i === 4 || i === 5 ? 'rgba(127,119,221,0.25)' : 'rgba(127,119,221,0.1)'),
      borderColor: '#7F77DD',
      borderWidth: 1.5,
      borderRadius: 2,
    }],
  };

  return (
    <Box>
      <SectionHead>Audience</SectionHead>
      <Grid4>
        <KpiCard label="New users" value={fmtK(newUsers)} subtitle={totalActive > 0 ? `${Math.round(newUsers / totalActive * 100)}% of active` : ''} />
        <KpiCard label="Returning users" value={fmtK(returningUsers)} subtitle={totalActive > 0 ? `${Math.round(returningUsers / totalActive * 100)}% of active` : ''} />
        <KpiCard label="Total active" value={fmtK(totalActive)} subtitle="this period" />
        <KpiCard label="Return rate" value={returnRate} subtitle="target > 60%" subtitleColor="pos" />
      </Grid4>

      <>
          <SectionHead sx={{ marginTop: '4px' }}>Requests by service type</SectionHead>
          <Card sx={{ marginBottom: '14px' }}>
            {services.length === 0 ? (
              <Box sx={{ textAlign: 'center', color: '#9b9b97', fontSize: '12px', fontFamily: '"SF Mono", "Fira Code", monospace', padding: '32px 0' }}>
                No requests in this period
              </Box>
            ) : (
              <>
                <CardTitle>Service mix — {fmtK(svcTotal)} total</CardTitle>

                {/* Bar chart */}
                <Box sx={{ height: 180, mb: '14px' }}>
                  <Bar data={serviceChartData} options={chartOptions} />
                </Box>

                {/* Segmented bar */}
                <Box sx={{ display: 'flex', gap: '6px', height: '10px', borderRadius: '4px', overflow: 'hidden', mb: '10px' }}>
                  {services.map((svc, i) => (
                    <Box key={svc.name} sx={{ width: `${(svc.count / svcTotal * 100).toFixed(1)}%`, height: '100%', backgroundColor: SVC_COLORS[i % SVC_COLORS.length] }} />
                  ))}
                </Box>

                {/* Legend */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: '14px' }}>
                  {services.map((svc, i) => (
                    <Box key={svc.name} sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b6b68' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: SVC_COLORS[i % SVC_COLORS.length], flexShrink: 0 }} />
                      <span>{svc.name} — {fmtK(svc.count)}</span>
                    </Box>
                  ))}
                </Box>

                {/* Service tiles */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {services.map((svc, i) => (
                    <Box key={svc.name} sx={{ padding: '10px 12px', backgroundColor: SVC_LIGHT[i % SVC_LIGHT.length], borderRadius: '8px' }}>
                      <Typography sx={{ fontSize: '10px', color: SVC_COLORS[i % SVC_COLORS.length], fontFamily: '"SF Mono", "Fira Code", monospace', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '4px' }}>
                        {svc.name}
                      </Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 500, color: SVC_COLORS[i % SVC_COLORS.length] }}>
                        {fmtK(svc.count)}
                      </Typography>
                      <Typography sx={{ fontSize: '10px', color: SVC_COLORS[i % SVC_COLORS.length], opacity: 0.7, fontFamily: '"SF Mono", "Fira Code", monospace', mt: '2px' }}>
                        {(svc.count / svcTotal * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Card>
      </>

      {(byHour.length > 0 || byDay.length > 0) && (
        <>
          <SectionHead>Volume patterns</SectionHead>
          <Grid2>
            {byHour.length > 0 && (
              <Card>
                <CardTitle>Requests by hour of day</CardTitle>
                <Box sx={{ height: 160 }}>
                  <Bar data={hourChartData} options={chartOptions} />
                </Box>
              </Card>
            )}
            {byDay.length > 0 && (
              <Card>
                <CardTitle>Requests by day of week</CardTitle>
                <Box sx={{ height: 160 }}>
                  <Bar data={dayChartData} options={chartOptions} />
                </Box>
              </Card>
            )}
          </Grid2>
        </>
      )}
    </Box>
  );
}
